/* eslint-disable @typescript-eslint/no-explicit-any */
import { SortDirectionEnum } from '@gripp/shared-logic';
import { InfiniteDataGrid } from '@gripp/shared-ui';
import { ColumnDef } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaColumns } from './useMediaColumns';

const FIND_ASSIGNABLE_MEDIA = `
  query FindAssignableMedia($filter: FindAssignableMediaFilter!, $page: PageRequest, $orderBy: OrderByInput) {
    media {
      findAssignableMedia(filter: $filter, page: $page, orderBy: $orderBy) {
        count
        limit
        offset
        items {
          id
          name
          categories {
            name
          }
        }
      }
    }
  }
`;

export type AddonMediaAssignmentListProps = {
  workspaceId: string;
  addonId: string;
  maxHeight?: number;
  keyword?: string;
  mediaAssignments: string[];
  onMediaAssignmentsChange: (assignments: string[]) => void;
};

export const AddonMediaAssignmentList = ({
  workspaceId,
  addonId,
  maxHeight,
  keyword,
  mediaAssignments,
  onMediaAssignmentsChange,
}: AddonMediaAssignmentListProps) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<any>(() => {
    if (workspaceId) {
      return {
        keyword,
        addonId,
        workspaceId,
        // removed assignments from filter to prevent refetch on each toggle
      };
    }
    return undefined;
  });
  const columns = useMediaColumns() as ColumnDef<any, any>[];

  useEffect(() => {
    setFilter({
      keyword,
      addonId,
      workspaceId,
    });
  }, [keyword, addonId, workspaceId]);

  // Memoize the isRowSelected function to ensure it updates when mediaAssignments changes
  const isRowSelected = (row: any) => {
    return mediaAssignments.includes(row.id);
  };

  if (!filter) return null;

  return (
    <InfiniteDataGrid
      modelName="media"
      hideResultsLabel={true}
      query={FIND_ASSIGNABLE_MEDIA}
      filter={filter}
      pageSize={100} // Much larger page size to ensure all assigned files are loaded
      columns={columns}
      listMaxHeight={maxHeight as number}
      containerStyle={{ height: maxHeight }}
      paddingBottom={0}
      textEmpty={t('addOn.files.textEmpty')}
      dataPrefix="media.findAssignableMedia"
      orderBy={{ field: 'name', order: SortDirectionEnum.Asc }}
      updateRowsSelected={(rows) => {
        onMediaAssignmentsChange(rows);
      }}
      isRowSelected={isRowSelected}
      enableScrollRefresh={true}
      showScrollIndicator={true}
    />
  );
};
